import { useState, useEffect, useContext } from "react";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { SideSheet } from "../../../components/sheet/sideSheet";
import { ActivityCard } from "../../../components/card/activityCard";
import { Select } from "../../../components/select/index";

const ActivityDetails = ({
  isOpen,
  mode,
  editingActivity,
  selectedActivityId,
  closeModal,
  timeOptions,
  shiftStartTime,
  shiftEndTime,
  onSave,
  customerIds = [],
}) => {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();

  // Local state for form fields
  const [timingType, setTimingType] = useState("selected"); // 'selected' or 'adHoc'
  const [activityFilter, setActivityFilter] = useState("all"); // 'all', 'checkpoints', 'tours'
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [search, setSearch] = useState("");
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null); // Track selected activity

  // Fetch activities when the sheet opens
  useEffect(() => {
    if (isOpen && orgId) {
      setLoading(true);
      authFetch(`${process.env.REACT_APP_API_URL}/v1/activities?org_id=${orgId}`)
        .then((res) => res.json())
        .then((data) => {
          // Deduplicate activities by ID to prevent React key warnings
          const uniqueActivities = data.filter(
            (activity, index, self) =>
              index === self.findIndex((a) => a.id === activity.id)
          );
          setActivities(uniqueActivities);
        })
        .catch(() => setActivities([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen, orgId]);

  // Initialize state on open
  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && editingActivity) {
      setTimingType(editingActivity.adHoc ? "adHoc" : "selected");
      setStart(editingActivity.start || "");
      setEnd(editingActivity.end || "");
      setSearch("");
      // Set the selected activity for editing
      setSelectedActivity({
        id: editingActivity.activityId, // Use activityId (checkpoint/tour ID) not id (shift_definition_activity ID)
        type: editingActivity.type,
        label: editingActivity.label,
        // Add other activity properties as needed
      });
    } else if (mode === "add" && editingActivity?.slot) {
      setTimingType(editingActivity.slot.isAdHoc ? "adHoc" : "selected");
      // If selected time, preselect the slot hour as start
      if (!editingActivity.slot.isAdHoc) {
        const slotLabel = editingActivity.slot.label;
        const match = slotLabel.match(/(\d{1,2}):(\d{2})/);
        if (match) {
          let hour = parseInt(match[1], 10);
          // Labels are 24-hour; still accept an AM/PM label if one turns up.
          const ampm = slotLabel.match(/\b(AM|PM)\b/i)?.[1]?.toUpperCase();
          if (ampm === "PM" && hour !== 12) hour += 12;
          if (ampm === "AM" && hour === 12) hour = 0;

          const hour24 = hour.toString().padStart(2, "0");
          // Quarter-hour slots carry the exact minute in their label (e.g. 08:15);
          // top-of-hour slots use "00".
          const minute = match[2];

          // Check if this is the first hour slot and shift starts at a non-full hour
          // If so, use the actual shift start time as default, otherwise use the mark
          let defaultStart;
          if (
            minute === "00" &&
            hour24 === editingActivity.shiftStartTime?.split(":")[0] &&
            editingActivity.shiftStartTime?.split(":")[1] !== "00"
          ) {
            // Shift starts at this hour but not at the top (e.g., 8:45 AM)
            // Use the actual shift start time as default
            defaultStart = editingActivity.shiftStartTime;
          } else {
            // Top-of-hour or quarter-hour mark — honor the label's minute
            defaultStart = `${hour24}:${minute}`;
          }

          const defaultEnd = getNextTimeIncrement(defaultStart);

          setStart(defaultStart);
          setEnd(defaultEnd);
        } else {
          setStart("");
          setEnd("");
        }
      } else {
        setStart("");
        setEnd("");
      }
      setSearch("");
      setSelectedActivity(null); // No activity selected for add mode initially
    } else {
      setTimingType("selected");
      // Set default times when initializing in selected mode
      const defaultStart = shiftStartTime || "08:00";
      const defaultEnd = getNextTimeIncrement(defaultStart);
      setStart(defaultStart);
      setEnd(defaultEnd);
      setSearch("");
      setSelectedActivity(null);
    }
    setActivityFilter("all");
  }, [isOpen, mode, editingActivity, shiftStartTime, shiftEndTime]);

  // Helper function to get the next time increment
  const getNextTimeIncrement = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes;
    const nextMinutes = totalMinutes + 15;
    const nextHours = Math.floor(nextMinutes / 60);
    const nextMins = nextMinutes % 60;
    return `${String(nextHours).padStart(2, "0")}:${String(nextMins).padStart(
      2,
      "0"
    )}`;
  };

  // Handle start time change
  const handleStartTimeChange = (newStartTime) => {
    setStart(newStartTime);

    // If end time is now invalid (before or equal to start time), adjust it
    if (end && end <= newStartTime) {
      const newEndTime = getNextTimeIncrement(newStartTime);
      setEnd(newEndTime);
    }
  };

  // Handle timing type change
  const handleTimingTypeChange = (newTimingType) => {
    setTimingType(newTimingType);

    // If switching from ad hoc to selected time and no times are set, set default times
    if (newTimingType === "selected" && (!start || start === "")) {
      // Set default times based on shift start time or current time
      const defaultStart = shiftStartTime || "08:00";
      const defaultEnd = getNextTimeIncrement(defaultStart);
      setStart(defaultStart);
      setEnd(defaultEnd);
    }
  };

  // Helper function to check if a shift spans overnight (end time is before start time)
  const isOvernightShift = (startTime, endTime) => {
    const startMins =
      parseInt(startTime.split(":")[0]) * 60 +
      parseInt(startTime.split(":")[1]);
    const endMins =
      parseInt(endTime.split(":")[0]) * 60 + parseInt(endTime.split(":")[1]);
    return endMins < startMins;
  };

  // Helper function to check if a time is within shift bounds (handles overnight shifts)
  const isTimeInShiftBounds = (time, startTime, endTime) => {
    const timeMins =
      parseInt(time.split(":")[0]) * 60 + parseInt(time.split(":")[1]);
    const startMins =
      parseInt(startTime.split(":")[0]) * 60 +
      parseInt(startTime.split(":")[1]);
    const endMins =
      parseInt(endTime.split(":")[0]) * 60 + parseInt(endTime.split(":")[1]);

    if (isOvernightShift(startTime, endTime)) {
      // For overnight shifts: time must be >= start OR <= end
      return timeMins >= startMins || timeMins <= endMins;
    } else {
      // For regular shifts: time must be >= start AND <= end
      return timeMins >= startMins && timeMins <= endMins;
    }
  };

  // Helper function to filter time options based on shift times
  const getFilteredTimeOptions = (isEndTime = false) => {
    if (!shiftStartTime || !shiftEndTime) return timeOptions;
    const minDuration = 15; // minutes

    // Helper to convert HH:mm to minutes
    const timeToMinutes = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const shiftStartMins = timeToMinutes(shiftStartTime);
    const shiftEndMins = timeToMinutes(shiftEndTime);
    const startMins = start ? timeToMinutes(start) : null;
    const endMins = end ? timeToMinutes(end) : null;

    if (isEndTime) {
      // End time: after start, up to and including shift end
      return timeOptions.filter((option) => {
        const optionMins = timeToMinutes(option.value);
        // Must be after start and at least minDuration after start
        const validAfterStart = !startMins || optionMins > startMins;
        const validMinDuration =
          !startMins || optionMins - startMins >= minDuration;
        // Must be within shift bounds (not after shift end)
        const withinShift = optionMins <= shiftEndMins;
        return validAfterStart && validMinDuration && withinShift;
      });
    } else {
      // Start time: from shift start up to (shift end - minDuration)
      const filteredOptions = timeOptions.filter((option) => {
        const optionMins = timeToMinutes(option.value);
        // Must be within shift bounds
        const withinShift =
          optionMins >= shiftStartMins &&
          optionMins <= shiftEndMins - minDuration;
        return withinShift;
      });

      return filteredOptions;
    }
  };

  // Handle Save
  const handleSave = () => {
    if (!selectedActivity) {
      console.warn("No activity selected, cannot save");
      return;
    }

    // Validate times for selected timing type
    if (timingType === "selected" && start && end) {
      if (start >= end) {
        console.warn("Start time must be before end time");
        // You could show a toast/alert here if you have a notification system
        return;
      }
    }

    // Determine if this should be ad hoc based on timing type and start time
    const shouldBeAdHoc =
      timingType === "adHoc" ||
      (timingType === "selected" && (!start || start === ""));

    const activityData = {
      ...selectedActivity,
      timingType,
      start: shouldBeAdHoc ? null : start,
      end: shouldBeAdHoc ? null : end,
      adHoc: shouldBeAdHoc,
      // Ensure activityId is preserved for backend - this should be the checkpoint/tour ID
      activityId: selectedActivity.id, // This is now correctly the checkpoint/tour ID
      // CRITICAL FIX: Preserve the original shift_definition_activity ID when editing
      // This prevents data loss when moving activities between ad hoc and scheduled
      id: mode === "edit" && editingActivity ? editingActivity.id : Date.now(),
    };

    if (onSave) onSave(activityData);
    // Don't close modal here - let parent component handle it after state update
  };

  // Filter activities for the current tab
  const filteredActivities = activities.filter((a) => {
    // First filter by type
    if (activityFilter === "checkpoints" && a.type !== "checkpoint")
      return false;
    if (activityFilter === "tours" && a.type !== "tour") return false;
    // Then by the shift's customers (none picked means All)
    if (
      customerIds.length > 0 &&
      !customerIds.some((id) => String(id) === String(a.customer?.id))
    )
      return false;
    // Then filter by search term (label only)
    if (search) {
      const searchLower = search.toLowerCase();
      return a.label && a.label.toLowerCase().includes(searchLower);
    }
    return true;
  });

  // Check if times are valid for save button
  const isTimeValid = timingType === "adHoc" || !start || !end || start < end;
  const isSaveDisabled =
    !selectedActivity || (timingType === "selected" && !isTimeValid);

  return (
    <SideSheet
      isOpen={isOpen}
      onClose={closeModal}
      title="Select activity"
      footerText="Select"
      handleSave={handleSave}
      isForm={false}
      primaryButtonDisabled={isSaveDisabled}
      buttonLayout="auto"
    >
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Search and filter row */}
        <div className="flex items-center mb-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search for activity..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block justify-between gap-x-1.5 w-full rounded-md px-3 py-2.5 items-center text-sm leading-none font-normal text-gray-500 dark:text-gray-300 border-0 ring-1 ring-gray-100 dark:ring-gray-700 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-150 bg-gray-50 dark:bg-gray-800 focus:ring-2 focus:ring-gray-100 dark:focus:ring-gray-600 dark:placeholder-gray-500"
            />
          </div>
          <div className="flex space-x-0.5 ml-3 bg-gray-100 dark:bg-gray-800 rounded-md p-0.5">
            <button
              className={`px-2 py-1 rounded-md text-xs font-regular transition-colors duration-150 ${
                activityFilter === "all"
                  ? "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-white/[0.06]"
              }`}
              onClick={() => setActivityFilter("all")}
              type="button"
            >
              All
            </button>
            <button
              className={`px-2 py-1 rounded-md text-xs font-regular transition-colors duration-150 ${
                activityFilter === "checkpoints"
                  ? "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-white/[0.06]"
              }`}
              onClick={() => setActivityFilter("checkpoints")}
              type="button"
            >
              Checkpoints
            </button>
            <button
              className={`px-2 py-1 rounded-md text-xs font-regular transition-colors duration-150 ${
                activityFilter === "tours"
                  ? "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-white/[0.06]"
              }`}
              onClick={() => setActivityFilter("tours")}
              type="button"
            >
              Tours
            </button>
          </div>
        </div>
        {/* Type and time pickers on one line */}
        <div className="flex items-center mb-3">
          <div className="w-1/3 pr-2">
            <label className="sr-only">Type</label>
            <Select
              name="timingType"
              label="Type"
              options={[
                { value: "selected", label: "Selected time" },
                { value: "adHoc", label: "Ad hoc" },
              ]}
              value={timingType}
              onChange={(e) => handleTimingTypeChange(e.target.value)}
              useForm={false}
            />
          </div>
          {timingType === "selected" && (
            <>
              <div className="w-1/3 px-1">
                <label className="sr-only">Start Time</label>
                <Select
                  name="start"
                  label="Start Time"
                  options={getFilteredTimeOptions()}
                  value={start}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  useForm={false}
                />
              </div>
              <div className="w-1/3 pl-2">
                <label className="sr-only">End Time</label>
                <Select
                  name="end"
                  label="End Time"
                  options={getFilteredTimeOptions(true)}
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  useForm={false}
                />
                {timingType === "selected" && start && end && start >= end && (
                  <div className="text-xs text-danger mt-1">
                    End time must be after start time
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {/* Activity cards list */}
        <div className="">
          {loading ? (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-md py-8">
              Loading activities...
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 bg-gray-50 dark:bg-gray-800 rounded-md">
              <ClipboardDocumentListIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">No activities found</p>
            </div>
          ) : (
            filteredActivities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                isSelected={selectedActivity?.id === activity.id}
                onClick={() => {
                  // Handle activity selection
                  setSelectedActivity(activity);
                }}
              />
            ))
          )}
        </div>
      </div>
    </SideSheet>
  );
};

export { ActivityDetails };
